import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Loader2 } from 'lucide-react';
import OnboardingWizardContent from '@/components/onboarding/OnboardingWizardContent';

interface TenantData {
  id: string;
  name: string;
  industry: string | null;
  owner_email: string;
  status: string | null;
}

const ClientOnboarding: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    checkAccess();
  }, [tenantId]);

  const checkAccess = async () => {
    if (!tenantId) {
      toast({ title: 'Error', description: 'Invalid tenant ID', variant: 'destructive' });
      navigate('/');
      return;
    }

    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({ title: 'Access Denied', description: 'Please log in to continue', variant: 'destructive' });
        navigate('/admin/login');
        return;
      }

      setUserId(session.user.id);

      // Check if user has access to this tenant (client_viewer with matching tenant_id OR admin/operator)
      const { data: roleData, error: roleError } = await supabase
        .from('advisory_user_roles')
        .select('role, tenant_id')
        .eq('user_id', session.user.id);

      if (roleError) {
        console.error('Error checking roles:', roleError);
        toast({ title: 'Error', description: 'Failed to verify access', variant: 'destructive' });
        navigate('/');
        return;
      }

      // Check if user is staff (admin/operator) or has client_viewer role for this tenant
      const isStaff = roleData?.some(r => 
        (r.role === 'admin' || r.role === 'operator') && r.tenant_id === null
      );
      const isTenantMember = roleData?.some(r => 
        r.tenant_id === tenantId
      );

      if (!isStaff && !isTenantMember) {
        // Also check if user is tenant owner
        const { data: tenantOwnerCheck } = await supabase
          .from('advisory_tenants')
          .select('owner_user_id')
          .eq('id', tenantId)
          .single();

        if (tenantOwnerCheck?.owner_user_id !== session.user.id) {
          toast({ title: 'Access Denied', description: 'You do not have permission to access this page', variant: 'destructive' });
          navigate('/');
          return;
        }
      }

      // Fetch tenant data
      const { data: tenantData, error: tenantError } = await supabase
        .from('advisory_tenants')
        .select('id, name, industry, owner_email, status')
        .eq('id', tenantId)
        .single();

      if (tenantError || !tenantData) {
        toast({ title: 'Error', description: 'Tenant not found', variant: 'destructive' });
        navigate('/');
        return;
      }

      setTenant(tenantData);
      setAuthorized(true);
    } catch (error) {
      console.error('Access check error:', error);
      toast({ title: 'Error', description: 'An error occurred', variant: 'destructive' });
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-brand-teal" />
            <p className="mt-4 text-muted-foreground">Verifying access...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!authorized || !tenant || !userId) {
    return null;
  }

  return (
    <>
      <Navbar />
      <main className="pt-16 min-h-screen bg-background">
        <OnboardingWizardContent tenant={tenant} userId={userId} />
      </main>
      <Footer />
    </>
  );
};

export default ClientOnboarding;
